<div class="p-4 lg:p-8">
  @if (isLoading()) {
    <div class="space-y-6">
      <div class="flex justify-between items-start gap-6">
        <div
          class="border border-gray-200 rounded-xl p-6 w-64 animate-pulse bg-gray-100"
        ></div>
        <div class="w-40 h-10 animate-pulse bg-gray-100 rounded-lg"></div>
      </div>
      <div class="h-8 w-32 animate-pulse bg-gray-100 rounded"></div>
      <div class="space-y-4">
        @for (i of [1, 2, 3]; track i) {
          <div
            class="border border-gray-200 rounded-xl p-6 h-48 animate-pulse bg-gray-100"
          ></div>
        }
      </div>
    </div>
  } @else {
    @if (reviews()!.length === 0) {
      <div class="text-center py-12 text-gray-500">
        <p class="text-lg font-medium">{{ 'reviewsPage.noReviews' | translate }}</p>
        <p class="text-sm">{{ 'reviewsPage.beFirst' | translate }}</p>
      </div>
    } @else {
      <div class="space-y-6">
        <!-- First Line: Rating Summary (left) and Filter Button (right) -->
        <div
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6"
          [class.justify-end]="!reviewsCount()"
        >
          <!-- Rating Summary Box -->
          @if (reviewsCount()) {
            <div
              class="border border-gray-200 rounded-xl p-6 h-auto sm:h-20 w-full sm:w-auto flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-11 justify-between"
            >
              <div class="w-full flex justify-between items-center gap-11">
                <span
                  class="text-black text-4xs sm:text-xs font-semibold px-3 py-1 rounded-md"
                  >{{ 'myReviews.reviews' | translate }}</span
                >
                <span
                  class="text-2xl sm:text-5xl font-bold tracking-tight text-gray-900"
                  >{{ reviewsAverage() }}</span
                >
                <div class="gap-1 text-[#A7651B] hidden sm:flex">
                  @for (star of getStarArray(reviewsAverage()); track star) {
                    <span
                      class="flex text-yellow-400 **:stroke-current h-6 w-6 sm:gap-x-1.5"
                    >
                      <sham-i-RatingStar />
                    </span>
                  }
                </div>
                <p
                  class="text-3xs sm:text-sm font-normal text-[#3E484D] whitespace-nowrap"
                >
                  ({{ reviewsCount() }}
                  {{ 'reviewsPage.communityFeedback' | translate }})
                </p>
              </div>
            </div>
          }

          <!-- Filter Button -->
          <div
            class="relative hidden md:flex items-center justify-center bg-[#F3F4F6] gap-1 rounded-lg"
          >
            <button
              class="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <a
                class="cursor-pointer text-md font-medium"
                [routerLink]="['/product-reviews', productSlug()]"
              >
                {{ 'myReviews.viewAllReviews' | translate }}
              </a>
            </button>

            @if (showFilterDropdown()) {
              <div
                class="absolute top-full right-0 mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-48 p-2"
              >
                <div
                  class="px-4 py-2 cursor-pointer rounded-md hover:bg-gray-50 transition-colors"
                  (click)="filterByRating(null)"
                  [class.bg-sky-100]="selectedFilter() === null"
                  [class.text-sky-700]="selectedFilter() === null"
                >
                  {{ 'reviewsPage.allRatings' | translate }}
                </div>
                @for (rating of [5, 4, 3, 2, 1]; track rating) {
                  <div
                    class="px-4 py-2 cursor-pointer rounded-md hover:bg-gray-50 transition-colors"
                    (click)="filterByRating(rating)"
                    [class.bg-sky-100]="selectedFilter() === rating"
                    [class.text-sky-700]="selectedFilter() === rating"
                  >
                    {{ rating }} {{ 'reviewsPage.stars' | translate }}
                  </div>
                }
              </div>
            }
          </div>
        </div>

        <!-- Second Line: Comments Title -->
        <h2 class="text-xl font-semibold text-gray-900 capitalize">
          {{ 'reviewsPage.comments' | translate }}
        </h2>

        <!-- Third Line: Review Cards -->
        <div class="space-y-4">
          @if (reviews()!.length === 0 && !isLoading()) {
            <div class="text-center py-12 text-gray-500">
              <p class="text-lg font-medium">
                {{ 'reviewsPage.noReviews' | translate }}
              </p>
            </div>
          } @else {
            @for (review of reviews(); track review.id; let last = $last) {
              <div
                class="flex flex-col xl:flex-row justify-between gap-8 py-8 border-b border-[#E6E6E6]"
              >
                <!-- LEFT -->
                <div class="flex-1">
                  <div class="flex items-start gap-4">
                    <!-- Avatar -->
                    @if (review.userAvatar) {
                      <img
                        [src]="review.userAvatar"
                        alt="{{ review.userName }}"
                        class="w-12 h-12 rounded-full border border-gray-200 object-cover shrink-0"
                      />
                    } @else {
                      <div
                        class="w-12 h-12 rounded-full bg-[#2596BE] text-white font-semibold flex items-center justify-center shrink-0"
                      >
                        {{ getInitials(review.userName) }}
                      </div>
                    }

                    <div class="flex-1">
                      <!-- Name + Date -->
                      <div class="flex flex-wrap items-center gap-2">
                        <h4 class="text-base font-semibold text-gray">
                          {{ review.userName }}
                        </h4>

                        <span class="text-[#BDBDBD]">•</span>

                        <span class="text-sm text-[#777777]">
                          {{ formatDate(review.createdAt) }}
                        </span>
                      </div>

                      <!-- Rating -->
                      <div class="flex items-center gap-2 mt-2">
                        <span class="text-sm font-semibold text-gray">
                          {{ review.rating.toFixed(1) }}
                        </span>

                        <div class="flex gap-1 text-[#F4B400]">
                          @for (star of getStarArray(review.rating); track star) {
                            <span
                              class="flex text-yellow-400 **:stroke-current h-4 w-4 sm:gap-x-1.5"
                            >
                              <sham-i-RatingStar />
                            </span>
                          }
                        </div>
                      </div>

                      <!-- Comment -->
                      <p class="mt-4 text-sm leading-7 text-[#5B5B5B]">
                        {{ review.content }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- RIGHT -->
                @if (review.images && review.images.length > 0) {
                  <div class="flex shrink-0">
                    <div class="flex gap-3 overflow-x-auto max-w-[320px]">
                      @for (image of review.images; track image.id) {
                        <img
                          [src]="appEnv.MEDIA_BASE_URL() + '/' + image.url"
                          alt="Review image"
                          class="w-24 h-24 rounded-md border border-[#E6E6E6] object-cover flex-shrink-0"
                        />
                      }
                    </div>
                  </div>
                }
              </div>
            }
          }
        </div>
      </div>
    }

    <!-- Pagination -->
    @if (totalPages() > 1) {
      <div class="flex items-center justify-end pt-8">
        <div class="flex items-center gap-4">
          <!-- Current Page Dropdown -->
          <div class="relative">
            <button
              (click)="showPageDropdown.set(!showPageDropdown())"
              class="flex items-center gap-2 rounded-md border border-[#E6E6E6] bg-white px-4 py-2 text-sm font-medium h-6 text-[#555]"
            >
              {{ currentPage() }}

              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            @if (showPageDropdown()) {
              <div
                class="absolute right-0 mt-2 w-20 rounded-lg border border-[#E6E6E6] bg-white shadow-lg z-50"
              >
                @for (page of pageNumbers(); track page) {
                  <button
                    (click)="goToPage(page)"
                    class="block w-full px-4 py-2 text-left hover:bg-gray-50"
                    [class.font-semibold]="page === currentPage()"
                  >
                    {{ page }}
                  </button>
                }
              </div>
            }
          </div>

          <span class="text-sm text-[#666]">
            {{ 'reviewsPage.of' | translate }} {{ totalPages() }}
            {{ 'reviewsPage.pages' | translate }}
          </span>

          <!-- Previous -->
          <div class="flex items-center gap-2">
            <button
              (click)="goToPreviousPage()"
              [disabled]="currentPage() === 1"
              class=""
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.4716 11.5289C10.7319 11.7893 10.7319 12.2114 10.4716 12.4717C10.2112 12.7321 9.78911 12.7321 9.52876 12.4717L5.52876 8.47173C5.27637 8.21934 5.26754 7.81295 5.50873 7.54984L9.1754 3.54984C9.42419 3.27843 9.8459 3.2601 10.1173 3.50889C10.3887 3.75769 10.4071 4.1794 10.1583 4.45081L6.92292 7.98028L10.4716 11.5289Z"
                  fill="#555555"
                />
              </svg>
            </button>

            <!-- Next -->
            <button
              (click)="goToNextPage()"
              [disabled]="currentPage() === totalPages()"
              class=""
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.52843 4.47108C5.26808 4.21073 5.26808 3.78862 5.52843 3.52827C5.78878 3.26792 6.21089 3.26792 6.47124 3.52827L10.4712 7.52827C10.7236 7.78066 10.7325 8.18705 10.4913 8.45016L6.82461 12.4502C6.57581 12.7216 6.1541 12.7399 5.88269 12.4911C5.61127 12.2423 5.59294 11.8206 5.84173 11.5492L9.07708 8.01972L5.52843 4.47108Z"
                  fill="#555555"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    }
  }
</div>
